import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { Tooltip } from 'react-tooltip'

import styles from './SubscriptionsInfo.module.scss'

import TitledCard from 'components/elements/blocks/TitledCard'
import CustomButton from 'components/elements/buttons/CustomButton'
import PlanStatus from 'components/elements/blocks/PlanStatus'
import TopUpBalanceModal from 'components/modules/pricing/TopUpBalanceModal'
import FullActivationModal from 'components/modules/pricing/FullActivationModal'

import {
  selectUserOverviewFetching,
  selectUserSubscriptionView,
  selectUserBalanceView,
} from 'store/user/selectors'
import { selectTopUpModalVisibility } from 'store/pricing/selectors'
import { PricingActionsTypes } from 'store/pricing/actions'
import { getNormalizedPrice } from 'utils/functions'
import { IUserSubscription } from 'models/user'
import { logAmplitudeEvent } from 'utils/analytics'

interface ISubscriptionInfo {
  reducedDisplayMode?: boolean
}

const SubscriptionsInfo: React.FC<ISubscriptionInfo> = (props) => {
  const { reducedDisplayMode } = props

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const balanceInfo = useSelector(selectUserBalanceView)
  const subscriptionInfo: IUserSubscription = useSelector(
    selectUserSubscriptionView
  )
  const userOverviewFetching = useSelector(selectUserOverviewFetching)
  const isTopUpModalShown = useSelector(selectTopUpModalVisibility)

  const [activeTopUpTabIndex, setActiveTopUpTabIndex] = useState(0)
  const [isFullActivationModalShown, toggleFullActivationModal] =
    useState(false)

  const balanceData = !!balanceInfo
    ? {
        balance: getNormalizedPrice(balanceInfo.balance),
        accrued: getNormalizedPrice(balanceInfo.accrued),
      }
    : {
        balance: 'n/a',
        accrued: 'n/a',
      }

  const handleCloseTopUpModal = () => {
    dispatch({
      type: PricingActionsTypes.PRICING_TOGGLE_TOP_UP_MODAL,
      isTopUpModalShown: false,
    })

    setActiveTopUpTabIndex(0)
  }

  const handleShowTopUpModal = () => {
    dispatch({
      type: PricingActionsTypes.PRICING_TOGGLE_TOP_UP_MODAL,
      isTopUpModalShown: true,
    })
  }

  const handleTrialHelp = () => {
    logAmplitudeEvent('click Trial help')

    window.open(
      'https://helpcenter.soax.com/en/collections/2651783-getting-started-with-soax',
      '_blank',
      'noopener,noreferrer'
    )
  }

  const handleCloseFullActivationModal = () => {
    toggleFullActivationModal(false)
  }

  /*const chartOptions = {
    cutout: '80%',
    rotation: 0,
    plugins: {
      legend: {
        display: false,
      },
    },
    tooltip: false,
    responsive: true,
    maintainAspectRatio: false,
  }

  const chartData = {
    datasets: [
      {
        data: [balanceData.used, balanceData.remain],
        backgroundColor: ['#E9EAEC', '#26BD6C'],
        borderWidth: 0,
      },
    ],
  }

  const emptyChartData = {
    datasets: [
      {
        data: [100],
        backgroundColor: ['#E9EAEC'],
        borderWidth: 0,
      },
    ],
  }*/

  // const handleGoToPricingClick = () => navigate('/pricing')

  const handleChoosePlanClick = () => {
    logAmplitudeEvent('choose plan')
    navigate('/pricing')
  }

  const handleActivateFullPlan = () => {
    logAmplitudeEvent('click Activate-full started')

    toggleFullActivationModal(true)
  }

  const handleTopUpTabChange = (index: number) => {
    setActiveTopUpTabIndex(index)
  }

  const handleManageTopUp = () => {
    if (isTopUpActive) {
      setActiveTopUpTabIndex(1)
      handleShowTopUpModal()
    } else {
      return null
    }
  }

  const isTopUpActive = balanceInfo?.top_up_access

  /*const topUpBtnColor =
    subscriptionInfo?.status === 'active' ? 'outline' : 'blue'*/

  const renderBtn = () => {
    if (
      subscriptionInfo?.status === 'trial' &&
      !!subscriptionInfo?.full_plan_id
    ) {
      return (
        <CustomButton
          onClick={handleActivateFullPlan}
          loading={userOverviewFetching}
        >
          Activate full plan now
        </CustomButton>
      )
    } else if (
      !subscriptionInfo?.status ||
      subscriptionInfo?.status === 'expired' ||
      subscriptionInfo?.status === 'cancelled'
    ) {
      return (
        <CustomButton onClick={handleChoosePlanClick}>Choose plan</CustomButton>
      )
    } else {
      return null
    }
  }

  const renderTopUpBtn = () => {
    if (subscriptionInfo?.status === 'trial') {
      return (
        <div>
          <div data-tip data-tooltip-id={`tooltip__trial-help`}>
            <CustomButton
              onClick={handleTrialHelp}
              color={'outline'}
              className={styles.topUpBtn}
            >
              Trial help
            </CustomButton>
          </div>

          <Tooltip id={`tooltip__trial-help`} place={'bottom'}>
            Need help setting up your trial?
          </Tooltip>
        </div>
      )
    } else if (balanceInfo?.out_of_balance) {
      return (
        <div>
          <div data-tip data-tooltip-id={`tooltip__top-up-notice`}>
            <CustomButton
              onClick={handleShowTopUpModal}
              // disabled={!isTopUpActive}
              color={'outline'}
              className={styles.topUpBtn}
            >
              Top up balance
            </CustomButton>
          </div>

          <Tooltip id={`tooltip__top-up-notice`} place={'bottom'}>
            insufficient funds, click to top up
          </Tooltip>
        </div>
      )
    } else {
      return (
        <CustomButton
          onClick={handleShowTopUpModal}
          disabled={!isTopUpActive}
          color={'outline'}
          className={styles.topUpBtn}
        >
          Top up balance
        </CustomButton>
      )
    }
  }

  const messagesList = !!subscriptionInfo?.sub_statuses?.length
    ? subscriptionInfo.sub_statuses?.map((message, index) => (
        <div className={styles.message} key={`subscription-message_${index}`}>
          <p>{message.title}</p>
          {/*{!!message.url && (
            <p>
              <a
                href={message.url}
                target={'_blank'}
                rel={'noopener noreferrer'}
                className={styles.messageLink}
              >
                click here for more details
              </a>
            </p>
          )}*/}
        </div>
      ))
    : null

  const handleMessageUrlClick = (message: {
    title: string
    url?: string | null
  }) => {
    if (!!message.url) {
      logAmplitudeEvent('click Pay invoice')

      window.open(message.url, '_blank', 'noopener,noreferrer')
    }
  }

  const renderMessagesBtns = () => {
    if (!!subscriptionInfo?.sub_statuses?.length) {
      return subscriptionInfo.sub_statuses?.map((message, index) => {
        if (message.url) {
          return (
            <CustomButton
              color={'blue'}
              className={styles.invoiceBtn}
              key={`issued_invoice_btn-${index}`}
              onClick={() => handleMessageUrlClick(message)}
            >
              Pay invoice
            </CustomButton>
          )
        } else {
          return null
        }
      })
    }
  }

  const renderMessages = () => {
    if (!!subscriptionInfo?.sub_statuses?.length) {
      return <div className={styles.messagesList}>{messagesList}</div>
    } else {
      return null
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        <TitledCard title={'Your subscription plan'} className={styles.card}>
          <div className={styles.cardContent}>
            <div className={styles.planInfo}>
              <div className={styles.planName}>
                {subscriptionInfo?.status
                  ? subscriptionInfo.plan
                  : 'No active subscription'}
              </div>

              {subscriptionInfo?.status && (
                <PlanStatus
                  status={subscriptionInfo?.status}
                  activationStatus={subscriptionInfo?.activation_status}
                  className={styles.planStatus}
                />
              )}
            </div>

            {renderMessages()}

            <div className={styles.btnsBlock}>
              {renderMessagesBtns()}

              {renderBtn()}
            </div>

            {subscriptionInfo?.status === 'blocked' && (
              <a href={'/'} className={styles.contactLink}>
                Please contact support
              </a>
            )}
          </div>
        </TitledCard>

        {!reducedDisplayMode && (
          <TitledCard title={'Balance'} className={styles.card}>
            <div className={styles.cardContent}>
              {/*<div className={styles.balanceInfo}>
                  Spent this month ${balanceData.accrued}
                </div>*/}
              {/*{!!balanceData && balanceData.total > 0 ? (
                <div className={styles.balanceInfo}>
                  Used ${balanceData.used} of ${balanceData.total}
                </div>
              ) : (
                <div className={styles.balanceInfo}>Used $0 of $0</div>
              )}*/}

              <div className={styles.rowBlock}>
                <div className={styles.balanceMain}>${balanceData.balance}</div>

                <div className={styles.topUpBlock}>
                  {!!balanceInfo?.top_up_params ? (
                    <div
                      className={`
                      ${styles.activeTopUpStatus}
                      ${isTopUpActive ? styles.clickable : ''}
                    `}
                      onClick={handleManageTopUp}
                    >
                      Auto top up ON
                    </div>
                  ) : (
                    <div
                      className={`
                      ${styles.inactiveTopUpStatus}
                      ${isTopUpActive ? styles.clickable : ''}
                    `}
                      onClick={handleManageTopUp}
                    >
                      Auto top up OFF
                    </div>
                  )}
                </div>
              </div>

              {renderTopUpBtn()}
            </div>
          </TitledCard>
        )}
      </div>

      <TopUpBalanceModal
        isModalShown={isTopUpModalShown}
        activeTopUpTabIndex={activeTopUpTabIndex}
        onActiveTopUpTabIndex={handleTopUpTabChange}
        onModalClose={handleCloseTopUpModal}
      />

      {!!subscriptionInfo?.full_plan_id && (
        <FullActivationModal
          isModalShown={isFullActivationModalShown}
          fullPlanPrice={subscriptionInfo.full_plan_price}
          fullPlanId={subscriptionInfo.full_plan_id}
          onModalClose={handleCloseFullActivationModal}
        />
      )}
    </div>
  )
}

export default SubscriptionsInfo
